package ru.chm.gradereport.ui.screens.course.sheet

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import ru.chm.gradereport.model.GradeSheetDto
import ru.chm.gradereport.model.HomeworkDto
import ru.chm.gradereport.model.HomeworkSubmissionItemDto
import ru.chm.gradereport.model.LessonSessionDto
import ru.chm.gradereport.model.StudentDto
import ru.chm.gradereport.ui.components.AppCard
import ru.chm.gradereport.ui.components.AppTextField
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.SecondaryButton
import ru.chm.gradereport.ui.components.TabChip
import ru.chm.gradereport.ui.components.formatDisplayDate
import ru.chm.gradereport.ui.theme.BlueAccent
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.GreenAccent
import ru.chm.gradereport.ui.theme.YellowAccent

private enum class SubmissionStatusFilter(val label: String) {
    ALL("Все"),
    GRADED("Оценены"),
    UNGRADED("Не оценены"),
}

private const val ALL_SESSIONS: String = "__all__"

/**
 * All submitted homeworks grouped by student, with filters by student name,
 * graded state and lesson topic (session).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun SubmissionsDialog(
    sheet: GradeSheetDto,
    editable: Boolean,
    onDismiss: () -> Unit,
    onEditSubmission: (submissionId: String, url: String) -> Unit,
    onDeleteSubmission: (submissionId: String) -> Unit,
) {
    var nameFilter by remember { mutableStateOf("") }
    var statusFilter by remember { mutableStateOf(SubmissionStatusFilter.ALL) }
    val studentById = remember(sheet.students) {
        sheet.students.associateBy { it.id }
    }
    val homeworkById = remember(sheet.homeworks) {
        sheet.homeworks.associateBy { it.id }
    }
    val gradedCells = remember(sheet.grades) {
        sheet.grades
            .filter { it.score != null }
            .map { it.studentId to it.homeworkId }
            .toSet()
    }
    val sessionsNewestFirst = remember(sheet.sessions) {
        sheet.sessions.sortedWith(
            compareByDescending<LessonSessionDto> { it.date }
                .thenByDescending { it.title }
                .thenByDescending { it.id },
        )
    }

    // Default to the newest topic; keep "all" only when the course has no sessions.
    var sessionFilter by remember(sheet.sessions) {
        mutableStateOf(sessionsNewestFirst.firstOrNull()?.id ?: ALL_SESSIONS)
    }

    val filteredSubmissions = remember(
        sheet.submissions,
        nameFilter,
        statusFilter,
        sessionFilter,
        gradedCells,
    ) {
        val query = nameFilter.trim()
        val hwIdsInSession = sessionFilter.takeIf { it != ALL_SESSIONS }?.let { sid ->
            sheet.homeworks.filter { it.sessionId == sid }.map { it.id }.toSet()
        }
        sheet.submissions.filter { submission ->
            val studentName = studentById[submission.studentId]?.fullName.orEmpty()
            val nameOk = query.isEmpty() || studentName.contains(query, ignoreCase = true)
            val sessionOk = hwIdsInSession == null || submission.homeworkId in hwIdsInSession
            val graded = (submission.studentId to submission.homeworkId) in gradedCells
            val statusOk = when (statusFilter) {
                SubmissionStatusFilter.ALL -> true
                SubmissionStatusFilter.GRADED -> graded
                SubmissionStatusFilter.UNGRADED -> !graded
            }
            nameOk && sessionOk && statusOk
        }
    }

    // One card per student; newest answers first inside.
    val studentGroups = remember(filteredSubmissions, studentById) {
        filteredSubmissions
            .groupBy { it.studentId }
            .map { (studentId, subs) ->
                StudentSubmissionGroup(
                    student = studentById[studentId],
                    submissions = subs.sortedByDescending { it.createdAt },
                )
            }
            .sortedBy { group -> group.student?.fullName?.lowercase().orEmpty() }
    }

    val colors = GradeTheme.colors
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false),
    ) {
        AppCard(
            modifier = Modifier
                .widthIn(min = 520.dp, max = 760.dp)
                .fillMaxWidth(0.94f)
                .heightIn(max = 720.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        "Сданные ДЗ",
                        color = colors.textPrimary,
                        fontWeight = FontWeight.Bold,
                        fontSize = 20.sp,
                    )
                    MutedText(
                        "Ответы из форм, сгруппированы по студентам: ${sheet.submissions.size}",
                        modifier = Modifier.padding(top = 4.dp),
                    )
                }
                SecondaryButton(text = "Закрыть", onClick = onDismiss)
            }

            AppTextField(
                value = nameFilter,
                onValueChange = { nameFilter = it },
                label = "Поиск по ФИО",
                modifier = Modifier.padding(top = 12.dp),
            )

            FlowRow(
                modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                SubmissionStatusFilter.entries.forEach { filter ->
                    TabChip(
                        text = filter.label,
                        selected = statusFilter == filter,
                        onClick = { statusFilter = filter },
                    )
                }
            }

            if (sessionsNewestFirst.isNotEmpty()) {
                Text(
                    "Тема",
                    color = colors.textMuted,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.padding(top = 10.dp),
                )
                FlowRow(
                    modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TabChip(
                        text = "Все темы",
                        selected = sessionFilter == ALL_SESSIONS,
                        onClick = { sessionFilter = ALL_SESSIONS },
                    )
                    sessionsNewestFirst.forEach { session ->
                        TabChip(
                            text = "${formatDisplayDate(session.date)} · ${session.title}",
                            selected = sessionFilter == session.id,
                            onClick = { sessionFilter = session.id },
                        )
                    }
                }
            }

            MutedText(
                "Показано: ${filteredSubmissions.size} ответов · ${studentGroups.size} студентов",
                modifier = Modifier.padding(top = 10.dp),
            )

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(max = 420.dp)
                    .verticalScroll(rememberScrollState())
                    .padding(top = 6.dp),
            ) {
                if (sheet.submissions.isEmpty()) {
                    MutedText("Пока нет сданных ответов.")
                } else if (filteredSubmissions.isEmpty()) {
                    MutedText("Ничего не найдено по выбранным фильтрам.")
                } else {
                    studentGroups.forEach { group ->
                        StudentSubmissionsCard(
                            group = group,
                            homeworkById = homeworkById,
                            gradedCells = gradedCells,
                            editable = editable,
                            onEditSubmission = onEditSubmission,
                            onDeleteSubmission = onDeleteSubmission,
                        )
                    }
                }
            }
        }
    }
}

private data class StudentSubmissionGroup(
    val student: StudentDto?,
    val submissions: List<HomeworkSubmissionItemDto>,
)

@Composable
private fun StudentSubmissionsCard(
    group: StudentSubmissionGroup,
    homeworkById: Map<String, HomeworkDto>,
    gradedCells: Set<Pair<String, String>>,
    editable: Boolean,
    onEditSubmission: (submissionId: String, url: String) -> Unit,
    onDeleteSubmission: (submissionId: String) -> Unit,
) {
    val colors = GradeTheme.colors
    val shape = RoundedCornerShape(12.dp)
    val unseenCount = group.submissions.count { !it.seen }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = 10.dp)
            .clip(shape)
            .background(colors.nestedCard, shape)
            .border(1.dp, colors.border, shape)
            .padding(12.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                group.student?.fullName.orEmpty().ifBlank { "Неизвестный студент" },
                color = BlueAccent,
                fontWeight = FontWeight.SemiBold,
                fontSize = 15.sp,
                modifier = Modifier.weight(1f, fill = false),
            )
            if (unseenCount > 0) {
                Text(
                    "новых: $unseenCount",
                    color = Color.White,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier
                        .clip(RoundedCornerShape(8.dp))
                        .background(SubmissionAccent.copy(alpha = 0.85f))
                        .padding(horizontal = 8.dp, vertical = 3.dp),
                )
            }
        }

        // All answers of this student, merged per homework.
        group.submissions
            .groupBy { it.homeworkId }
            .forEach { (homeworkId, homeworkSubs) ->
                val homework = homeworkById[homeworkId]
                val title = homework?.title.orEmpty().ifBlank { "ДЗ (удалено)" }
                val graded = group.student?.let { (it.id to homeworkId) in gradedCells } == true
                Row(
                    modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        "ДЗ · $title",
                        color = YellowAccent,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier.weight(1f, fill = false),
                    )
                    Text(
                        if (graded) "Оценено" else "Не оценено",
                        color = if (graded) GreenAccent else colors.textMuted,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 13.sp,
                    )
                }
                Spacer(Modifier.height(2.dp))
                homeworkSubs.forEach { submission ->
                    SubmissionRow(
                        url = submission.url,
                        canEdit = editable,
                        onEdit = { url -> onEditSubmission(submission.id, url) },
                        onDelete = if (editable) {
                            { onDeleteSubmission(submission.id) }
                        } else {
                            null
                        },
                    )
                }
            }
    }
}
